import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { smartDisplayQty } from '@/lib/unitConverter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, Search, Edit2, Trash2, Droplets, Package2, 
  Box, Tag, ArrowUpDown, 
  ChevronDown, ChevronRight, FileText, Shield, Sparkles, X, ChevronUp, Building2,
  Printer
} from 'lucide-react';
import KursBar from '@/components/hpp/KursBar';
import { useKurs } from '@/hooks/useKurs';
import PackagingForm from '@/components/stok/PackagingForm';
import FullsetForm from '@/components/stok/FullsetForm';
import StokBahanForm from '@/components/stok/StokBahanForm';
import ItemTambahanForm from '@/components/stok/ItemTambahanForm';
import CetakSampleStokModal from '@/components/stok/CetakSampleStokModal';
import LastUpdated from '@/components/ui/LastUpdated';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/lib/SessionContext';
import { getPageAccess, getShowPriceAccess } from '@/lib/AuthSession';
import { useAsaConfirm } from '@/contexts/AsaConfirmContext';
import { toast } from 'react-hot-toast';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';

const fmt = (n) => n != null ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '—';

const TABS = [
  { key: 'packaging', label: 'Packaging', icon: Package2, desc: 'Botol, tutup & spray' },
  { key: 'bahan', label: 'Bahan Cair', icon: Droplets, desc: 'Bibit, alkohol & pelarut' },
  { key: 'item-tambahan', label: 'Item Tambahan', icon: Tag, desc: 'Stiker, Box, sablon, hot print, gift card & lainnya' },
  { key: 'legalitas-jasa', label: 'Legalitas & Jasa', icon: Shield, desc: 'BPOM, HAKI, Jasa Proses & Packing' },
];

const PACKAGING_TYPE_BADGE = {
  botol: { label: 'Botol', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  tutup: { label: 'Tutup', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  spray: { label: 'Spray', cls: 'bg-green-100 text-green-700 border-green-200' },
  fullset: { label: 'Fullset', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const ITEM_KATEGORI_BADGE = {
  Stiker: { cls: 'bg-pink-100 text-pink-700' },
  Box: { cls: 'bg-amber-100 text-amber-700' },
  Sablon: { cls: 'bg-indigo-100 text-indigo-700' },
  'Hot Print': { cls: 'bg-yellow-100 text-yellow-700' },
  'Gift Card': { cls: 'bg-emerald-100 text-emerald-700' },
  Lainnya: { cls: 'bg-slate-100 text-slate-700' },
};

const ITEM_TAMBAHAN_KATEGORI = ['Stiker', 'Box', 'Sablon', 'Hot Print', 'Gift Card', 'Coloring', 'Lainnya'];
const BAHAN_KATEGORI = ['Bibit', 'Alkohol', 'Aqua Des', 'DPG', 'Peg', 'Sustain', 'Quidex', 'Castor Oil', 'Lainnya'];

const SORT_OPTIONS = [
  { key: 'terbaru', label: 'Terbaru' },
  { key: 'terlama', label: 'Terlama' },
  { key: 'vendor_az', label: 'Vendor: A → Z' },
  { key: 'vendor_za', label: 'Vendor: Z → A' },
  { key: 'termahal', label: 'Termahal' },
  { key: 'termurah', label: 'Termurah' },
  { key: 'abjad_az', label: 'A → Z' },
  { key: 'abjad_za', label: 'Z → A' },
  { key: 'stok_terbanyak', label: 'Stok Terbanyak' },
  { key: 'stok_tersedikit', label: 'Stok Tersedikit' },
];

// Sort helper
function applySort(list, sortKey, priceField = 'harga_rupiah') {
  const arr = [...list];
  switch (sortKey) {
    case 'terbaru': return arr.sort((a, b) => new Date(b.created_date || 0) - new Date(a.created_date || 0));
    case 'terlama': return arr.sort((a, b) => new Date(a.created_date || 0) - new Date(b.created_date || 0));
    case 'vendor_az': return arr.sort((a, b) => {
      const vA = (a.vendor || a.brand || (a.dari_client ? `Client (${a.client_owner || 'Client'})` : '') || '').toLowerCase();
      const vB = (b.vendor || b.brand || (b.dari_client ? `Client (${b.client_owner || 'Client'})` : '') || '').toLowerCase();
      if (vA !== vB) {
        if (!vA) return 1;
        if (!vB) return -1;
        return vA.localeCompare(vB, 'id');
      }
      return (a.nama || '').localeCompare(b.nama || '', 'id');
    });
    case 'vendor_za': return arr.sort((a, b) => {
      const vA = (a.vendor || a.brand || (a.dari_client ? `Client (${a.client_owner || 'Client'})` : '') || '').toLowerCase();
      const vB = (b.vendor || b.brand || (b.dari_client ? `Client (${b.client_owner || 'Client'})` : '') || '').toLowerCase();
      if (vA !== vB) {
        if (!vA) return 1;
        if (!vB) return -1;
        return vB.localeCompare(vA, 'id');
      }
      return (a.nama || '').localeCompare(b.nama || '', 'id');
    });
    case 'termahal': return arr.sort((a, b) => (b[priceField] || 0) - (a[priceField] || 0));
    case 'termurah': return arr.sort((a, b) => (a[priceField] || 0) - (b[priceField] || 0));
    case 'abjad_az': return arr.sort((a, b) => (a.nama || '').localeCompare(b.nama || '', 'id'));
    case 'abjad_za': return arr.sort((a, b) => (b.nama || '').localeCompare(a.nama || '', 'id'));
    case 'stok_terbanyak': return arr.sort((a, b) => (b.stok || b.total_stock || 0) - (a.stok || a.total_stock || 0));
    case 'stok_tersedikit': return arr.sort((a, b) => (a.stok || a.total_stock || 0) - (b.stok || b.total_stock || 0));
    default: return arr;
  }
}

function SortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find(s => s.key === value);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-600 hover:border-[#8C6208]/60 transition-all"
      >
        <ArrowUpDown className="w-3.5 h-3.5" />
        {current?.label || 'Urutkan'}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[150px]">
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => { onChange(opt.key); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${value === opt.key ? 'text-[#8C6208] font-bold' : 'text-slate-700'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function StokHPP() {
  const queryClient = useQueryClient();
  const { confirm } = useAsaConfirm();
  const { kurs } = useKurs();
  const navigate = useNavigate();

  const portal = useMemo(() => {
    const match = window.location.pathname.match(/\/office\/([^\/]+)/);
    return match ? match[1] : 'finance';
  }, []);
  const isProductionView = portal === 'production';
  const isPurchasingView = portal === 'purchasing';

  const visibleTabs = useMemo(() => {
    return TABS.filter(t => {
      if (t.key === 'legalitas-jasa') {
        return !isProductionView && !isPurchasingView;
      }
      return true;
    });
  }, [isProductionView, isPurchasingView]);

  // Baca tab dari URL query param
  const urlTab = new URLSearchParams(window.location.search).get('tab');
  const [activeTab, setActiveTab] = useState(urlTab || 'packaging');
  
  useEffect(() => {
    if (activeTab === 'legalitas-jasa' && (isProductionView || isPurchasingView)) {
      setActiveTab('packaging');
    }
  }, [activeTab, isProductionView, isPurchasingView]);

  const { member } = useSession();
  const canEdit = member?.role !== 'marketing' && getPageAccess(member, "stok_inventori") === "edit";
  const canDelete = canEdit;
  const showPrice = getShowPriceAccess(member, "stok_inventori") || getShowPriceAccess(member, "purchasing_items") || getShowPriceAccess(member, "portal_purchasing");
  const [search, setSearch] = useState(() => {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      return p.get('search') || '';
    }
    return '';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      const q = p.get('search');
      if (q) setSearch(q);
    }
  }, []);
  const [showForm, setShowForm] = useState(false);
  const [showCetakSampleModal, setShowCetakSampleModal] = useState(false);
  const [sampleModalInitialItem, setSampleModalInitialItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editTipe, setEditTipe] = useState('botol');
  const [ownershipFilter, setOwnershipFilter] = useState('semua'); // 'semua', 'satala', 'titipan'
  const [statusFilter, setStatusFilter] = useState('semua'); // 'semua', 'aktif', 'pasif', 'nonaktif'
  const [packFilter, setPackFilter] = useState('semua');
  const [bahanFilter, setBahanFilter] = useState('semua');
  const [itemFilter, setItemFilter] = useState('semua');
  const [itemBrandFilter, setItemBrandFilter] = useState('semua');
  const [packSort, setPackSort] = useState('terbaru');
  const [bahanSort, setBahanSort] = useState('terbaru');
  const [itemSort, setItemSort] = useState('terbaru');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Sub-filter untuk Legalitas & Jasa
  const [legalitasJasaFilter, setLegalitasJasaFilter] = useState('semua');

  // Modal states untuk Legalitas & Jasa
  const [showLegalitasForm, setShowLegalitasForm] = useState(false);
  const [editLegalitas, setEditLegalitas] = useState(null);
  const [legalitasNama, setLegalitasNama] = useState('');
  const [legalitasHargaBeli, setLegalitasHargaBeli] = useState('');
  const [legalitasHargaJual, setLegalitasHargaJual] = useState('');
  const [legalitasCatatan, setLegalitasCatatan] = useState('');

  const [showJasaForm, setShowJasaForm] = useState(false);
  const [editJasa, setEditJasa] = useState(null);
  const [jasaNama, setJasaNama] = useState('');
  const [jasaMinMl, setJasaMinMl] = useState('');
  const [jasaMaxMl, setJasaMaxMl] = useState('');
  const [jasaHargaBeli, setJasaHargaBeli] = useState('');
  const [jasaHargaJual, setJasaHargaJual] = useState('');
  const [jasaCatatan, setJasaCatatan] = useState('');

  // Queries
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });
  const { data: bahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });

  const { data: legalitasList = [], refetch: refetchLegalitas } = useQuery({ queryKey: ['legalitas'], queryFn: () => base44.entities.Legalitas.list() });
  const { data: jasaList = [], refetch: refetchJasa } = useQuery({ queryKey: ['jasa'], queryFn: () => base44.entities.Jasa.list() });

  // Mutations
  const delBotol = useMutation({ mutationFn: id => base44.entities.Botol.delete(id), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['botol'] }) });
  const delTutup = useMutation({ mutationFn: id => base44.entities.Tutup.delete(id), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tutup'] }) });
  const delSpray = useMutation({ mutationFn: id => base44.entities.Spray.delete(id), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['spray'] }) });
  const delFullset = useMutation({ mutationFn: id => base44.entities.Fullset.delete(id), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fullset'] }) });
  const delBahan = useMutation({ mutationFn: id => base44.entities.BahanCair.delete(id), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bahan-cair'] }) });
  const delItem = useMutation({ mutationFn: id => base44.entities.ItemTambahan.delete(id), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['item-tambahan'] }) });

  const addLegalitas = useMutation({ mutationFn: d => base44.entities.Legalitas.create(d), onSuccess: () => { refetchLegalitas(); toast.success('Legalitas berhasil ditambahkan!'); } });
  const updateLegalitas = useMutation({ mutationFn: ({ id, data }) => base44.entities.Legalitas.update(id, data), onSuccess: () => { refetchLegalitas(); toast.success('Legalitas berhasil diubah!'); } });
  const deleteLegalitas = useMutation({ mutationFn: id => base44.entities.Legalitas.delete(id), onSuccess: () => { refetchLegalitas(); toast.success('Legalitas berhasil dihapus!'); } });

  const addJasa = useMutation({ mutationFn: d => base44.entities.Jasa.create(d), onSuccess: () => { refetchJasa(); toast.success('Jasa berhasil ditambahkan!'); } });
  const updateJasa = useMutation({ mutationFn: ({ id, data }) => base44.entities.Jasa.update(id, data), onSuccess: () => { refetchJasa(); toast.success('Jasa berhasil diubah!'); } });
  const deleteJasa = useMutation({ mutationFn: id => base44.entities.Jasa.delete(id), onSuccess: () => { refetchJasa(); toast.success('Jasa berhasil dihapus!'); } });

  // Auto-seed Legalitas & Jasa if database is empty
  useEffect(() => {
    if (activeTab === 'legalitas-jasa') {
      if (legalitasList.length === 0) {
        const defaults = [
          { nama: 'BPOM (Per Varian)', harga_beli: 500000, harga_jual: 2500000, catatan: 'Biaya resmi registrasi BPOM RI' },
          { nama: 'HAKI (Hak Kekayaan Intelektual)', harga_beli: 2000000, harga_jual: 3000000, catatan: 'Pendaftaran sertifikat merk' },
          { nama: 'Sertifikasi Halal', harga_beli: 1000000, harga_jual: 2000000, catatan: 'Sertifikasi Halal LPPOM MUI' }
        ];
        defaults.forEach(d => addLegalitas.mutate(d));
      }
      if (jasaList.length === 0) {
        const defaults = [
          { nama: 'Jasa Ukuran ≤ 10ml', ukuran_min_ml: 0, ukuran_max_ml: 10, harga_beli: 2000, harga_jual: 4000, catatan: 'Pengisian & packing serum kecil' },
          { nama: 'Jasa Ukuran ≤ 30ml', ukuran_min_ml: 11, ukuran_max_ml: 30, harga_beli: 5000, harga_jual: 10000, catatan: 'Standard filling & packing' },
          { nama: 'Jasa Ukuran ≤ 50ml', ukuran_min_ml: 31, ukuran_max_ml: 50, harga_beli: 8000, harga_jual: 15000, catatan: 'Medium bottle filling' },
          { nama: 'Jasa Ukuran > 50ml', ukuran_min_ml: 51, ukuran_max_ml: 1000, harga_beli: 10000, harga_jual: 20000, catatan: 'Large volume filling & labeling' }
        ];
        defaults.forEach(d => addJasa.mutate(d));
      }
    }
  }, [activeTab, legalitasList.length, jasaList.length]);

  const handleSaved = () => {
    setShowForm(false);
    setEditItem(null);
    ['botol', 'tutup', 'spray', 'fullset', 'bahan-cair', 'item-tambahan'].forEach(k =>
      queryClient.invalidateQueries({ queryKey: [k] })
    );
  };

  const packagingAll = [
    ...botolList.map(i => ({ ...i, _tipe: 'botol' })),
    ...tutupList.map(i => ({ ...i, _tipe: 'tutup' })),
    ...sprayList.map(i => ({ ...i, _tipe: 'spray' })),
    ...fullsetList.map(fs => ({ ...fs, _tipe: 'fullset', stok: fs.stok ?? 0 })),
  ];

  // Brand list untuk item tambahan
  const itemBrands = useMemo(() => {
    const set = new Set(itemTambahanList.map(i => i.brand).filter(Boolean));
    return Array.from(set).sort();
  }, [itemTambahanList]);

  const matchSearch = (item, query) => {
    if (!query) return true;
    const qTrim = query.trim().toLowerCase();
    const nama = (item.nama || '').toLowerCase();
    const vendor = (item.vendor || '').toLowerCase();
    const brand = (item.brand || '').toLowerCase();
    const clientOwner = (item.client_owner || item.client || '').toLowerCase();
    const titipanText = item.dari_client ? `titipan ${clientOwner} client` : '';
    const catatan = (item.catatan || '').toLowerCase();
    const catatanTeknis = (item.catatan_teknis || '').toLowerCase();
    const kategori = (item.kategori || '').toLowerCase();
    const grade = (item.grade || '').toLowerCase();

    return (
      nama.includes(qTrim) ||
      vendor.includes(qTrim) ||
      brand.includes(qTrim) ||
      clientOwner.includes(qTrim) ||
      titipanText.includes(qTrim) ||
      catatan.includes(qTrim) ||
      catatanTeknis.includes(qTrim) ||
      kategori.includes(qTrim) ||
      grade.includes(qTrim)
    );
  };

  const isTitipanItem = (item) => {
    return Boolean(
      item.dari_client || 
      item.vendor === 'Client' || 
      (item.client_owner && item.client_owner.trim()) ||
      (item.vendor && item.vendor.toLowerCase().includes('titipan')) ||
      (item.nama && item.nama.toLowerCase().includes('titipan'))
    );
  };

  const matchOwnership = (item) => {
    if (ownershipFilter === 'titipan') return isTitipanItem(item);
    if (ownershipFilter === 'satala') return !isTitipanItem(item);
    return true;
  };

  const matchStatus = (item) => {
    if (statusFilter === 'semua') return true;
    const st = item.status || 'aktif';
    return st === statusFilter;
  };

  const packagingFiltered = useMemo(() => {
    const filtered = packagingAll
      .filter(i => packFilter === 'semua' || i._tipe === packFilter)
      .filter(i => matchOwnership(i))
      .filter(i => matchStatus(i))
      .filter(i => matchSearch(i, search));
    return applySort(filtered, packSort);
  }, [packagingAll, packFilter, ownershipFilter, statusFilter, search, packSort]);

  const bahanFiltered = useMemo(() => {
    const filtered = bahanList
      .filter(i => bahanFilter === 'semua' || i.kategori === bahanFilter)
      .filter(i => matchOwnership(i))
      .filter(i => matchStatus(i))
      .filter(i => matchSearch(i, search));
    return applySort(filtered, bahanSort);
  }, [bahanList, bahanFilter, ownershipFilter, statusFilter, search, bahanSort]);

  const itemFiltered = useMemo(() => {
    const filtered = itemTambahanList
      .filter(i => itemFilter === 'semua' || i.kategori === itemFilter)
      .filter(i => itemBrandFilter === 'semua' || i.brand === itemBrandFilter)
      .filter(i => matchOwnership(i))
      .filter(i => matchStatus(i))
      .filter(i => matchSearch(i, search));
    return applySort(filtered, itemSort);
  }, [itemTambahanList, itemFilter, itemBrandFilter, ownershipFilter, statusFilter, search, itemSort]);

  // Filtering untuk Legalitas & Jasa
  const legalitasFiltered = useMemo(() => {
    const q = (search || '').toLowerCase();
    return legalitasList.filter(l => l.nama?.toLowerCase().includes(q) || l.catatan?.toLowerCase().includes(q));
  }, [legalitasList, search]);

  const jasaFiltered = useMemo(() => {
    const q = (search || '').toLowerCase();
    return jasaList.filter(j => j.nama?.toLowerCase().includes(q) || j.catatan?.toLowerCase().includes(q));
  }, [jasaList, search]);

  const currentList = activeTab === 'packaging' ? packagingFiltered : 
                      activeTab === 'bahan' ? bahanFiltered :
                      activeTab === 'item-tambahan' ? itemFiltered : 
                      activeTab === 'legalitas-jasa' ? [...legalitasFiltered, ...jasaFiltered] : [];
  
  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection
  } = useBatchSelection(currentList);

  useEffect(() => {
    clearSelection();
    setCurrentPage(1);
  }, [activeTab]);

  const handleBatchDelete = async () => {
    if (!await confirm(`Hapus ${selectedIds.size} item?`)) return;
    try {
      if (activeTab === 'packaging') {
        const toDelete = packagingFiltered.filter(i => selectedIds.has(i.id));
        await Promise.all(toDelete.map(i => {
          if (i._tipe === 'botol') return delBotol.mutateAsync(i.id);
          if (i._tipe === 'tutup') return delTutup.mutateAsync(i.id);
          if (i._tipe === 'fullset') return delFullset.mutateAsync(i.id);
          return delSpray.mutateAsync(i.id);
        }));
      } else if (activeTab === 'bahan') {
        await Promise.all(Array.from(selectedIds).map(id => delBahan.mutateAsync(id)));
      } else if (activeTab === 'item-tambahan') {
        await Promise.all(Array.from(selectedIds).map(id => delItem.mutateAsync(id)));
      } else if (activeTab === 'legalitas-jasa') {
        const ids = Array.from(selectedIds);
        const lIds = ids.filter(id => legalitasList.find(l => l.id === id));
        const jIds = ids.filter(id => jasaList.find(j => j.id === id));
        await Promise.all([
          ...lIds.map(id => deleteLegalitas.mutateAsync(id)),
          ...jIds.map(id => deleteJasa.mutateAsync(id))
        ]);
      }
      clearSelection();
      toast.success('Berhasil menghapus item terpilih');
    } catch (err) {
      console.error(err);
      toast.error('Gagal menghapus beberapa item');
    }
  };

  const getVendorName = (item) => {
    if (item.dari_client) {
      return item.client_owner ? `Titipan ${item.client_owner}` : 'Titipan Client';
    }
    if (item.vendor && item.vendor.trim()) return item.vendor.trim();
    if (item.brand && item.brand.trim()) return item.brand.trim();
    return 'Tanpa Vendor / Internal';
  };

  const groupItemsByVendor = (items) => {
    const groups = {};
    items.forEach(item => {
      const vName = getVendorName(item);
      if (!groups[vName]) groups[vName] = [];
      groups[vName].push(item);
    });
    return groups;
  };

  const paginatedPackaging = useMemo(() => packagingFiltered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage), [packagingFiltered, currentPage, itemsPerPage]);
  const groupedPackaging = useMemo(() => {
    const groups = { botol: [], tutup: [], spray: [], fullset: [] };
    paginatedPackaging.forEach(item => {
      if (groups[item._tipe]) {
        groups[item._tipe].push(item);
      }
    });
    return groups;
  }, [paginatedPackaging]);

  const paginatedBahan = useMemo(() => bahanFiltered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage), [bahanFiltered, currentPage, itemsPerPage]);
  const groupedBahan = useMemo(() => {
    const groups = {};
    BAHAN_KATEGORI.forEach(k => { groups[k] = []; });
    paginatedBahan.forEach(item => {
      const cat = item.kategori || 'Lainnya';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  }, [paginatedBahan]);

  const paginatedItemTambahan = useMemo(() => itemFiltered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage), [itemFiltered, currentPage, itemsPerPage]);
  const groupedItemTambahan = useMemo(() => {
    const groups = {};
    ITEM_TAMBAHAN_KATEGORI.forEach(k => { groups[k] = []; });
    paginatedItemTambahan.forEach(item => {
      const cat = item.kategori || 'Lainnya';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push({ ...item, _isItemTambahan: true });
    });
    return groups;
  }, [paginatedItemTambahan]);

  const renderItemCard = (item, tipe) => {
    const isSelected = selectedIds.has(item.id);
    const st = item.status || 'aktif';
    
    let bgClass = 'bg-white';
    if (st === 'aktif') bgClass = 'bg-emerald-50/30';
    else if (st === 'pasif') bgClass = 'bg-slate-100/50';
    else if (st === 'nonaktif') bgClass = 'bg-rose-50/50';

    return (
      <Card
        key={item.id + (item._tipe || '') + (item._isItemTambahan ? '-it' : '')}
        className={`transition-all ${bgClass} border-slate-200/80 ${isSelectionMode ? 'cursor-pointer hover:bg-slate-100/80' : 'cursor-pointer hover:border-[#8C6208]/40 hover:shadow-md'} ${isSelectionMode && isSelected ? 'ring-2 ring-rose-500 !bg-rose-50/80' : ''}`}
        onClick={() => {
          if (isSelectionMode) toggleSelection(item.id);
          else handleDetail(item, item._isItemTambahan ? 'item-tambahan' : (item._tipe || 'bahan'));
        }}
      >
        <CardContent className="p-3 md:p-4">
          <div className="flex items-start gap-2 md:gap-3">
            {isSelectionMode && activeTab !== 'legalitas-jasa' && (
              <div className="pt-1" onClick={e => e.stopPropagation()}>
                <Checkbox checked={isSelected} onCheckedChange={() => toggleSelection(item.id)} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {/* Packaging item */}
              {['botol', 'tutup', 'spray', 'fullset'].includes(item._tipe) && (() => {
                const badge = PACKAGING_TYPE_BADGE[item._tipe];
                return (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-slate-800">{item.nama}</span>
                      {st === 'aktif' && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200 shadow-none">Aktif</Badge>}
                      {st === 'pasif' && <Badge className="text-[10px] bg-slate-200 text-slate-600 border-slate-300 shadow-none">Pasif</Badge>}
                      {st === 'nonaktif' && <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200 shadow-none">Tidak Aktif</Badge>}
                      <Badge className={`text-[10px] font-black uppercase tracking-wider shadow-none ${badge.cls}`}>{badge.label}</Badge>
                      {item.dari_client && (
                        <Badge className="text-[10px] bg-sky-100 text-sky-700 border-sky-200">
                          Titipan {item.client_owner}
                        </Badge>
                      )}
                      {!item.dari_client && item.vendor && <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-200 shadow-none">{item.vendor}</Badge>}
                      {item._tipe === 'botol' && item.ukuran_label_ml && (
                        <Badge variant="outline" className="text-xs bg-slate-50">{item.ukuran_label_ml}ml</Badge>
                      )}
                      {item._tipe === 'botol' && item.ukuran_aktual_ml && item.ukuran_aktual_ml !== item.ukuran_label_ml && (
                        <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200">Aktual: {item.ukuran_aktual_ml}ml</Badge>
                      )}
                      {item._tipe === 'spray' && (
                        <Badge className={`text-xs ${item.tipe === 'Continuous' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-green-100 text-green-700 border-green-200'}`}>{item.tipe}</Badge>
                      )}
                      {item.ppn_type === 'exclude' ? (
                        <Badge className="text-[10px] bg-rose-50 text-rose-700 border-rose-200 shadow-none">Exclude PPN</Badge>
                      ) : item.ppn_type === 'include' ? (
                        <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 shadow-none">Include PPN</Badge>
                      ) : null}
                      {/* Currency Badge */}
                      {item.mata_uang === 'USD' ? (
                        <Badge className="text-[10px] font-black bg-amber-50 text-[#8C6208] border border-amber-300/80 shadow-none flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          USD ($)
                        </Badge>
                      ) : item.mata_uang === 'EUR' ? (
                        <Badge className="text-[10px] font-black bg-blue-50 text-blue-700 border border-blue-300/80 shadow-none flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          EUR (€)
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] font-black bg-emerald-50 text-emerald-800 border border-emerald-300/80 shadow-none flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          IDR (Rp)
                        </Badge>
                      )}
                    </div>
                    {/* Fullset component details */}
                    {item._tipe === 'fullset' && (() => {
                      const bNama = item.botol_id ? botolList.find(x => x.id === item.botol_id)?.nama : null;
                      const tNama = item.tutup_id ? tutupList.find(x => x.id === item.tutup_id)?.nama : null;
                      const sNama = item.spray_id ? sprayList.find(x => x.id === item.spray_id)?.nama : null;
                      const bStok = item.botol_id ? (botolList.find(x => x.id === item.botol_id)?.stok ?? '—') : null;
                      const tStok = item.tutup_id ? (tutupList.find(x => x.id === item.tutup_id)?.stok ?? '—') : null;
                      const sStok = item.spray_id ? (sprayList.find(x => x.id === item.spray_id)?.stok ?? '—') : null;
                      return (
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                          {bNama && <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">🫙 {bNama} <span className="font-bold">({bStok})</span></span>}
                          {tNama && <span className="text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">🔘 {tNama} <span className="font-bold">({tStok})</span></span>}
                          {sNama && <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded">💨 {sNama} <span className="font-bold">({sStok})</span></span>}
                        </div>
                      );
                    })()}
                    {item.catatan_teknis && <p className="text-xs text-amber-600 mt-0.5 italic flex items-center gap-1"><FileText className="w-3 h-3 shrink-0" />{item.catatan_teknis}</p>}
                    {item.catatan && <p className="text-xs text-slate-400 mt-0.5 italic">{item.catatan}</p>}
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500 flex-wrap">
                      {!isProductionView && showPrice && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Beli:</span>
                          {item.ppn_type === 'exclude' && item.harga_sebelum_ppn && (
                            <span className="flex items-center gap-1 font-bold text-slate-500 line-through decoration-rose-400/50 decoration-2">Rp {fmt(item.harga_sebelum_ppn)}</span>
                          )}
                          {item.mata_uang === 'EUR' && item.harga_euro ? (
                            <span className="flex items-center gap-1 text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded font-bold">€{item.harga_euro} <span className="opacity-60 text-slate-400">≈</span> Rp {fmt(item.harga_rupiah)}</span>
                          ) : (item.mata_uang === 'USD' && item.harga_dollar) ? (
                            <span className="flex items-center gap-1 text-[#8C6208] bg-amber-50 px-1.5 py-0.5 rounded font-bold">${item.harga_dollar} <span className="opacity-60 text-slate-400">≈</span> Rp {fmt(item.harga_rupiah)}</span>
                          ) : (
                            <span className="flex items-center gap-1 font-bold text-slate-700">
                              Rp {fmt(item.harga_rupiah)}
                              {item.harga_dollar && (
                                <span className="text-[11px] font-normal text-slate-400 ml-0.5">(≈ ${Number(item.harga_dollar).toFixed(2)})</span>
                              )}
                            </span>
                          )}
                          {item.harga_jual != null && (
                            <>
                              <span className="text-slate-200 font-light">|</span>
                              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Jual:</span>
                              <span className="flex items-center gap-1 text-green-700 bg-green-50 px-1.5 py-0.5 rounded font-bold">Rp {fmt(item.harga_jual)}</span>
                            </>
                          )}
                        </div>
                      )}
                      <span className={`font-bold ${item.stok != null && item.stok < 10 ? 'text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100' : 'text-slate-700 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100'}`}>
                        Stok: {item.stok ?? '—'} pcs
                      </span>

                    </div>
                    <div className="mt-1"><LastUpdated date={item.updated_date || item.created_date} /></div>
                  </>
                );
              })()}

              {/* Item Tambahan */}
              {item._isItemTambahan && (() => {
                const badge = ITEM_KATEGORI_BADGE[item.kategori] || ITEM_KATEGORI_BADGE['Lainnya'];
                return (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-slate-800">{item.nama}</span>
                      {st === 'aktif' && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200 shadow-none">Aktif</Badge>}
                      {st === 'pasif' && <Badge className="text-[10px] bg-slate-200 text-slate-600 border-slate-300 shadow-none">Pasif</Badge>}
                      {st === 'nonaktif' && <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200 shadow-none">Tidak Aktif</Badge>}
                      <Badge className={`text-xs shadow-none ${badge.cls}`}>{item.kategori}</Badge>
                      {item.dari_client && (
                        <Badge className="text-[10px] bg-sky-100 text-sky-700 border-sky-200">
                          Titipan {item.client_owner}
                        </Badge>
                      )}
                      {item.brand && <Badge className="text-xs bg-[#F9F5EC] text-[#8C6208] border border-[#E6D5B8]">{item.brand}</Badge>}
                      {!item.dari_client && item.vendor && <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-200">{item.vendor}</Badge>}
                      {item.ppn_type === 'exclude' ? (
                        <Badge className="text-[10px] bg-rose-50 text-rose-700 border-rose-200 shadow-none">Exclude PPN</Badge>
                      ) : item.ppn_type === 'include' ? (
                        <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 shadow-none">Include PPN</Badge>
                      ) : null}
                      {/* Currency Badge */}
                      {item.mata_uang === 'USD' ? (
                        <Badge className="text-[10px] font-black bg-amber-50 text-[#8C6208] border border-amber-300/80 shadow-none flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          USD ($)
                        </Badge>
                      ) : item.mata_uang === 'EUR' ? (
                        <Badge className="text-[10px] font-black bg-blue-50 text-blue-700 border border-blue-300/80 shadow-none flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          EUR (€)
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] font-black bg-emerald-50 text-emerald-800 border border-emerald-300/80 shadow-none flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          IDR (Rp)
                        </Badge>
                      )}
                    </div>
                    {item.catatan && <p className="text-xs text-slate-400 mt-0.5 italic">{item.catatan}</p>}
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500 flex-wrap">
                      {!isProductionView && showPrice && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Beli:</span>
                          {item.ppn_type === 'exclude' && item.harga_sebelum_ppn && (
                            <span className="flex items-center gap-1 font-bold text-slate-500 line-through decoration-rose-400/50 decoration-2">Rp {fmt(item.harga_sebelum_ppn)}/pcs</span>
                          )}
                          {item.mata_uang === 'EUR' && item.harga_euro ? (
                            <span className="flex items-center gap-1 text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded font-bold">€{item.harga_euro}/pcs <span className="opacity-60 text-slate-400">≈</span> Rp {fmt(item.harga_rupiah)}/pcs</span>
                          ) : (item.mata_uang === 'USD' && item.harga_dollar) ? (
                            <span className="flex items-center gap-1 text-[#8C6208] bg-amber-50 px-1.5 py-0.5 rounded font-bold">${item.harga_dollar}/pcs <span className="opacity-60 text-slate-400">≈</span> Rp {fmt(item.harga_rupiah)}/pcs</span>
                          ) : (
                            <span className="flex items-center gap-1 font-bold text-slate-700">
                              Rp {fmt(item.harga_rupiah)}/pcs
                              {item.harga_dollar && (
                                <span className="text-[11px] font-normal text-slate-400 ml-0.5">(≈ ${Number(item.harga_dollar).toFixed(2)})</span>
                              )}
                            </span>
                          )}
                          {item.harga_jual != null && (
                            <>
                              <span className="text-slate-200 font-light">|</span>
                              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Jual:</span>
                              <span className="flex items-center gap-1 text-green-700 bg-green-50 px-1.5 py-0.5 rounded font-bold">Rp {fmt(item.harga_jual)}/pcs</span>
                            </>
                          )}
                        </div>
                      )}
                      <span className={`font-bold ${item.stok != null && item.stok < 10 ? 'text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100' : 'text-slate-700 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100'}`}>
                        Stok: {item.stok ?? '—'} pcs
                      </span>

                    </div>
                    <div className="mt-1"><LastUpdated date={item.updated_date || item.created_date} /></div>
                  </>
                );
              })()}

              {/* Bahan cair item */}
              {!item._tipe && !item._isItemTambahan && (() => {
                return (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-slate-800">{item.nama}</span>
                      {st === 'aktif' && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200 shadow-none">Aktif</Badge>}
                      {st === 'pasif' && <Badge className="text-[10px] bg-slate-200 text-slate-600 border-slate-300 shadow-none">Pasif</Badge>}
                      {st === 'nonaktif' && <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200 shadow-none">Tidak Aktif</Badge>}
                      <Badge variant="outline" className="text-xs bg-white shadow-none">{item.kategori}</Badge>
                      {item.dari_client && (
                        <Badge className="text-[10px] bg-sky-100 text-sky-700 border-sky-200">
                          Titipan {item.client_owner}
                        </Badge>
                      )}
                      {!item.dari_client && item.vendor && <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-200 shadow-none">{item.vendor}</Badge>}
                      {item.grade && <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200 shadow-none">{item.grade}</Badge>}
                      {item.ppn_type === 'exclude' ? (
                        <Badge className="text-[10px] bg-rose-50 text-rose-700 border-rose-200 shadow-none">Exclude PPN</Badge>
                      ) : item.ppn_type === 'include' ? (
                        <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 shadow-none">Include PPN</Badge>
                      ) : null}
                      {/* Aesthetic Currency Badge */}
                      {item.mata_uang === 'USD' ? (
                        <Badge className="text-[10px] font-black bg-amber-50 text-[#8C6208] border border-amber-300/80 shadow-none flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          USD ($)
                        </Badge>
                      ) : item.mata_uang === 'EUR' ? (
                        <Badge className="text-[10px] font-black bg-blue-50 text-blue-700 border border-blue-300/80 shadow-none flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          EUR (€)
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] font-black bg-emerald-50 text-emerald-800 border border-emerald-300/80 shadow-none flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          IDR (Rp)
                        </Badge>
                      )}
                    </div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500 flex-wrap">
                    {!isProductionView && showPrice && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Beli:</span>
                        {item.ppn_type === 'exclude' && item.harga_sebelum_ppn && (
                          <span className="flex items-center gap-1 font-bold text-slate-500 line-through decoration-rose-400/50 decoration-2">Rp {fmt(item.harga_sebelum_ppn)}/{item.satuan}</span>
                        )}
                        {item.mata_uang === 'EUR' && item.harga_euro ? (
                          <span className="flex items-center gap-1 text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded font-bold">€{item.harga_euro}/{item.satuan} <span className="opacity-60 text-slate-400">≈</span> Rp {fmt(item.harga_rupiah)}/{item.satuan}</span>
                        ) : (item.mata_uang === 'USD' && item.harga_dollar) ? (
                          <span className="flex items-center gap-1 text-[#8C6208] bg-amber-50 px-1.5 py-0.5 rounded font-bold">${item.harga_dollar}/{item.satuan} <span className="opacity-60 text-slate-400">≈</span> Rp {fmt(item.harga_rupiah)}/{item.satuan}</span>
                        ) : (
                          <span className="flex items-center gap-1 font-bold text-slate-700">
                            Rp {fmt(item.harga_rupiah)}/{item.satuan}
                            {item.harga_dollar && (
                              <span className="text-[11px] font-normal text-slate-400 ml-0.5">(≈ ${Number(item.harga_dollar).toFixed(2)})</span>
                            )}
                          </span>
                        )}
                        {item.harga_jual != null && (
                          <>
                            <span className="text-slate-200 font-light">|</span>
                            <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Jual:</span>
                            <span className="flex items-center gap-1 text-green-700 bg-green-50 px-1.5 py-0.5 rounded font-bold">Rp {fmt(item.harga_jual)}/{item.satuan}</span>
                          </>
                        )}
                      </div>
                    )}
                    <span className={`font-bold ${item.stok != null && item.stok < 0.5 ? 'text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100' : 'text-slate-700 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100'}`}>
                      {(() => {
                        const { display, unit: du } = smartDisplayQty(item.stok, item.satuan);
                        return `Stok: ${display} ${du}`;
                      })()}
                    </span>
                  </div>
                  <div className="mt-1"><LastUpdated date={item.updated_date || item.created_date} /></div>
                </>
                );
              })()}
            </div>

            {/* Actions */}
            {canEdit && !isSelectionMode && (
              <div className="flex gap-1 shrink-0 items-center">
                <Button variant="ghost" size="icon" className="w-8 h-8 hover:bg-slate-100"
                  onClick={(e) => { e.stopPropagation(); handleEdit(item, item._isItemTambahan ? 'item-tambahan' : (item._tipe || 'bahan')); }}>
                  <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                </Button>
                {canDelete && (
                  <Button variant="ghost" size="icon" className="w-8 h-8 text-rose-500 hover:bg-rose-50"
                    onClick={(e) => { e.stopPropagation(); startSelection(item.id); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
            )}
            {!canEdit && !isSelectionMode && (
              <div className="flex gap-1 shrink-0 items-center">
                 <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const isTitipan = (i) => {
    if (!i) return false;
    if (i.dari_client === true) return true;
    if (i.vendor === 'Client' || (i.vendor || '').toLowerCase().includes('client')) return true;
    const n = (i.nama || '').toLowerCase();
    const c = (i.catatan || '').toLowerCase();
    const k = (i.kategori || '').toLowerCase();
    if (n.includes('titipan') || c.includes('titipan') || k.includes('titipan')) return true;
    return false;
  };



  const handleDelete = async (item) => {
    if (!await confirm(`Hapus ${item.nama}?`)) return;
    if (item._tipe === 'botol') delBotol.mutate(item.id);
    else if (item._tipe === 'tutup') delTutup.mutate(item.id);
    else if (item._tipe === 'spray') delSpray.mutate(item.id);
    else if (item._tipe === 'fullset') delFullset.mutate(item.id);
    else if (item._isItemTambahan) delItem.mutate(item.id);
    else delBahan.mutate(item.id);
  };

  const handleEdit = (item, tipe) => {
    const { _tipe, _isItemTambahan, ...cleanItem } = item;
    setEditItem(cleanItem);
    setEditTipe(tipe);
    setShowForm(true);
  };

  const handleDetail = (item, tipe) => {
    navigate(`/office/${portal}/stok-detail?id=${item.id}&tipe=${tipe}`);
  };

  const handleAdd = () => {
    if (activeTab === 'legalitas-jasa') {
      if (legalitasJasaFilter === 'jasa') {
        setEditJasa(null);
        setJasaNama('');
        setJasaMinMl('');
        setJasaMaxMl('');
        setJasaHargaBeli('');
        setJasaHargaJual('');
        setJasaCatatan('');
        setShowJasaForm(true);
      } else {
        setEditLegalitas(null);
        setLegalitasNama('');
        setLegalitasHargaBeli('');
        setLegalitasHargaJual('');
        setLegalitasCatatan('');
        setShowLegalitasForm(true);
      }
      return;
    }
    
    setEditItem(null);
    if (activeTab === 'bahan') setEditTipe('bahan');
    else if (activeTab === 'item-tambahan') setEditTipe('item-tambahan');
    else setEditTipe(packFilter !== 'semua' ? packFilter : 'botol');
    setShowForm(true);
  };

  const activeTabRawList = useMemo(() => {
    if (activeTab === 'packaging') {
      return packFilter === 'semua' ? packagingAll : packagingAll.filter(i => i._tipe === packFilter);
    }
    if (activeTab === 'bahan') {
      return bahanFilter === 'semua' ? bahanList : bahanList.filter(i => i.kategori === bahanFilter);
    }
    if (activeTab === 'item-tambahan') {
      let list = itemTambahanList;
      if (itemFilter !== 'semua') list = list.filter(i => i.kategori === itemFilter);
      if (itemBrandFilter !== 'semua') list = list.filter(i => i.brand === itemBrandFilter);
      return list;
    }
    return [];
  }, [activeTab, packagingAll, packFilter, bahanList, bahanFilter, itemTambahanList, itemFilter, itemBrandFilter]);

  const satalaCount = useMemo(() => {
    return activeTabRawList.filter(i => matchStatus(i) && !isTitipanItem(i)).length;
  }, [activeTabRawList, statusFilter]);

  const titipanCount = useMemo(() => {
    return activeTabRawList.filter(i => matchStatus(i) && isTitipanItem(i)).length;
  }, [activeTabRawList, statusFilter]);

  const aktifCount = useMemo(() => {
    return activeTabRawList.filter(i => matchOwnership(i) && (i.status || 'aktif') === 'aktif').length;
  }, [activeTabRawList, ownershipFilter]);

  const pasifCount = useMemo(() => {
    return activeTabRawList.filter(i => matchOwnership(i) && (i.status || 'aktif') === 'pasif').length;
  }, [activeTabRawList, ownershipFilter]);

  const nonaktifCount = useMemo(() => {
    return activeTabRawList.filter(i => matchOwnership(i) && (i.status || 'aktif') === 'nonaktif').length;
  }, [activeTabRawList, ownershipFilter]);

  const totalCount =
    activeTab === 'packaging' ? packagingAll.length :
    activeTab === 'item-tambahan' ? itemTambahanList.length :
    activeTab === 'legalitas-jasa' ? (legalitasList.length + jasaList.length) :
    bahanList.length;

  const currentSort = activeTab === 'packaging' ? packSort : activeTab === 'bahan' ? bahanSort : itemSort;
  const setCurrentSort = activeTab === 'packaging' ? setPackSort : activeTab === 'bahan' ? setBahanSort : setItemSort;

  return (
    <div className="space-y-5 text-[#302621]">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 md:gap-3">
        <div>
          <h1 className="text-xl md:text-3xl font-black text-slate-800 tracking-tight">Stok & Inventori</h1>
          <p className="text-slate-500 text-[9px] md:text-xs mt-0.5 md:mt-1">Kelola data packaging, formula bahan, legalitas BPOM/HAKI, dan range biaya jasa pabrik — {totalCount} item aktif.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isProductionView && showPrice && <KursBar />}
          <Button
            type="button"
            onClick={() => {
              setSampleModalInitialItem(null);
              setShowCetakSampleModal(true);
            }}
            className="flex items-center gap-2 h-9 px-3.5 rounded-xl border border-[#D4AF37]/50 bg-gradient-to-r from-[#FAF7F2] to-[#F5EFEB] hover:from-[#F3ECE1] hover:to-[#EADFCF] text-[#8C6208] text-xs font-black shadow-xs hover:shadow-sm transition-all cursor-pointer"
            title="Cetak Stiker Sample (40×20mm) untuk kedatangan bibit & item inventori"
          >
            <Printer className="w-4 h-4 text-[#8C6208]" />
            <span>Cetak Label Sample</span>
          </Button>
        </div>
      </div>

      {activeTab !== 'legalitas-jasa' && (
        <p className="text-[10px] font-bold text-slate-400 -mt-2">Klik card untuk melihat rincian riwayat penggunaan item berdasarkan brand produk.</p>
      )}



      {/* Tabs */}
      <div className="flex gap-2 flex-wrap items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {visibleTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setSearch(''); }}
                className={`flex items-center gap-1.5 md:gap-2 px-3 py-1.5 md:px-5 md:py-2.5 rounded-full text-[9px] md:text-xs font-black uppercase tracking-wider transition-all border ${
                  activeTab === tab.key 
                    ? 'bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8] shadow-sm' 
                    : 'bg-white text-slate-500 border-slate-200 hover:border-[#E6D5B8]'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Filter Controls (Ownership & Status) */}
        {activeTab !== 'legalitas-jasa' && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* Ownership Toggle (Semua, Satala, Titipan) */}
            <div className="flex items-center p-1 bg-slate-100/90 rounded-2xl border border-slate-200/80 shadow-xs">
              <button
                type="button"
                onClick={() => { setOwnershipFilter('semua'); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all ${
                  ownershipFilter === 'semua'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Semua ({activeTabRawList.length})
              </button>
              <button
                type="button"
                onClick={() => { setOwnershipFilter('satala'); setCurrentPage(1); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black transition-all ${
                  ownershipFilter === 'satala'
                    ? 'bg-[#8C6208] text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Building2 className="w-3.5 h-3.5" />
                <span>Satala ({satalaCount})</span>
              </button>
              <button
                type="button"
                onClick={() => { setOwnershipFilter('titipan'); setCurrentPage(1); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black transition-all ${
                  ownershipFilter === 'titipan'
                    ? 'bg-sky-600 text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Box className="w-3.5 h-3.5" />
                <span>Titipan ({titipanCount})</span>
              </button>
            </div>

            {/* Status Toggle (Semua Status, Aktif, Pasif, Nonaktif) */}
            <div className="flex items-center p-1 bg-slate-100/90 rounded-2xl border border-slate-200/80 shadow-xs">
              <button
                type="button"
                onClick={() => { setStatusFilter('semua'); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all ${
                  statusFilter === 'semua'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Semua Status
              </button>
              <button
                type="button"
                onClick={() => { setStatusFilter('aktif'); setCurrentPage(1); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black transition-all ${
                  statusFilter === 'aktif'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === 'aktif' ? 'bg-white' : 'bg-emerald-500'}`} />
                <span>Aktif ({aktifCount})</span>
              </button>
              <button
                type="button"
                onClick={() => { setStatusFilter('pasif'); setCurrentPage(1); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black transition-all ${
                  statusFilter === 'pasif'
                    ? 'bg-slate-700 text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === 'pasif' ? 'bg-white' : 'bg-slate-400'}`} />
                <span>Pasif ({pasifCount})</span>
              </button>
              <button
                type="button"
                onClick={() => { setStatusFilter('nonaktif'); setCurrentPage(1); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black transition-all ${
                  statusFilter === 'nonaktif'
                    ? 'bg-rose-600 text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === 'nonaktif' ? 'bg-white' : 'bg-rose-500'}`} />
                <span>Nonaktif ({nonaktifCount})</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sub-filter packaging */}
      {activeTab === 'packaging' && (
        <div className="flex gap-2 flex-wrap">
          {['semua', 'botol', 'tutup', 'spray', 'fullset'].map(f => (
            <button key={f} onClick={() => setPackFilter(f)}
              className={`px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all ${packFilter === f ? 'bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8] shadow-sm' : 'bg-white text-slate-500 border-slate-200'}`}
            >
              {f === 'semua' ? `Semua (${packagingAll.length})` : `${f} (${packagingAll.filter(i => i._tipe === f).length})`}
            </button>
          ))}
        </div>
      )}

      {/* Sub-filter bahan cair */}
      {activeTab === 'bahan' && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setBahanFilter('semua')}
            className={`px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all ${bahanFilter === 'semua' ? 'bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8] shadow-sm' : 'bg-white text-slate-500 border-slate-200'}`}
          >
            Semua ({bahanList.length})
          </button>
          {BAHAN_KATEGORI.map(k => (
            <button key={k} onClick={() => setBahanFilter(k)}
              className={`px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all ${bahanFilter === k ? 'bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8] shadow-sm' : 'bg-white text-slate-500 border-slate-200'}`}
            >
              {k} ({bahanList.filter(i => i.kategori === k).length})
            </button>
          ))}
        </div>
      )}

      {/* Sub-filter item tambahan — kategori + brand */}
      {activeTab === 'item-tambahan' && (
        <div className="space-y-2">
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setItemFilter('semua')}
              className={`px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all ${itemFilter === 'semua' ? 'bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8] shadow-sm' : 'bg-white text-slate-500 border-slate-200'}`}
            >
              Semua Kategori ({itemTambahanList.length})
            </button>
            {ITEM_TAMBAHAN_KATEGORI.map(k => (
              <button key={k} onClick={() => setItemFilter(k)}
                className={`px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all ${itemFilter === k ? 'bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8] shadow-sm' : 'bg-white text-slate-500 border-slate-200'}`}
              >
                {k} ({itemTambahanList.filter(i => i.kategori === k).length})
              </button>
            ))}
          </div>

        </div>
      )}

      {/* Sub-filter untuk Legalitas & Jasa */}
      {activeTab === 'legalitas-jasa' && (
        <div className="flex gap-2 flex-wrap">
          {['semua', 'legalitas', 'jasa'].map(f => (
            <button key={f} onClick={() => setLegalitasJasaFilter(f)}
              className={`px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all ${legalitasJasaFilter === f ? 'bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8] shadow-sm' : 'bg-white text-slate-500 border-slate-200'}`}
            >
              {f === 'semua' ? 'Semua Biaya' : f === 'legalitas' ? 'BPOM & HAKI' : 'Jasa Proses Pabrik'}
            </button>
          ))}
        </div>
      )}

      {/* Search + Sort + Add */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={
              activeTab === 'packaging' ? 'Cari packaging...' :
              activeTab === 'item-tambahan' ? 'Cari nama, brand, vendor...' :
              activeTab === 'legalitas-jasa' ? 'Cari nama legalitas atau range jasa...' :
              'Cari bahan atau vendor...'
            }
            className="pl-9 text-xs"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {activeTab !== 'legalitas-jasa' && <SortDropdown value={currentSort} onChange={setCurrentSort} />}
        {canEdit && (
          <Button onClick={handleAdd} className="gap-2 shrink-0 h-9 rounded-xl bg-[#302621] text-white hover:bg-[#1A1411] text-xs font-bold">
            <Plus className="w-4 h-4" /> 
            <span>{activeTab === 'legalitas-jasa' ? (legalitasJasaFilter === 'jasa' ? 'Tambah Jasa' : 'Tambah Legalitas') : 'Tambah Komponen'}</span>
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setSampleModalInitialItem(null);
            setShowCetakSampleModal(true);
          }}
          className="gap-2 shrink-0 h-9 rounded-xl border-[#8C6208]/40 bg-[#FAF7F2] hover:bg-[#F3ECE1] text-[#8C6208] text-xs font-bold shadow-xs cursor-pointer"
          title="Cetak Stiker Sample (40×20mm) untuk QC dan kedatangan barang"
        >
          <Printer className="w-4 h-4 text-[#8C6208]" />
          <span>Cetak Label Sample</span>
        </Button>
      </div>

      {/* List / Grid Renderers */}
      <div className="grid gap-3">
        
        {/* TAB 4: LEGALITAS & JASA PANEL */}
        {activeTab === 'legalitas-jasa' && (
          <div className="space-y-6">
            
            {/* 1. Legalitas BPOM / HAKI Section */}
            {(legalitasJasaFilter === 'semua' || legalitasJasaFilter === 'legalitas') && (
              <div className="space-y-3">
                <h3 className="text-sm font-black text-slate-800 flex items-center gap-1.5 uppercase tracking-wider">
                  <Shield className="w-4 h-4 text-[#8C6208]" />
                  Biaya Pengurusan Legalitas (BPOM / HAKI)
                </h3>
                {legalitasFiltered.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 bg-slate-50 border border-slate-200 border-dashed rounded-xl text-xs">
                    Belum ada biaya legalitas kustom.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {legalitasFiltered.map(l => (
                      <Card 
                        key={l.id} 
                        className={`border ${selectedIds.has(l.id) ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200/80'} hover:shadow-md transition-shadow relative overflow-hidden ${selectedIds.has(l.id) ? 'bg-primary/5' : 'bg-white'} ${isSelectionMode ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                        onClick={() => {
                          if (isSelectionMode) toggleSelection(l.id);
                        }}
                      >
                        {isSelectionMode && (
                          <div className="absolute top-3 left-3 z-10" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.has(l.id)}
                              onCheckedChange={() => toggleSelection(l.id)}
                              className="w-5 h-5 rounded-md data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                          </div>
                        )}
                        <div className="absolute top-0 right-0 p-3 text-[#E6D5B8] opacity-20 text-3xl font-black">🛡️</div>
                        <CardContent className="p-5 space-y-4">
                          <div>
                            <h4 className={`font-extrabold text-sm text-slate-800 truncate ${isSelectionMode ? 'ml-8' : ''}`} title={l.nama}>{l.nama}</h4>
                            <p className="text-[10px] text-slate-400 mt-1 line-clamp-2 italic h-7">{l.catatan || 'Tidak ada catatan teknis.'}</p>
                          </div>

                          <div className="bg-slate-50 rounded-xl p-3 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <p className="text-[9px] text-slate-400 font-bold uppercase">HPP (Modal Beli)</p>
                              <p className="font-black text-rose-600">Rp {fmt(l.harga_beli)}</p>
                            </div>
                            <div>
                              <p className="text-[9px] text-slate-400 font-bold uppercase">Harga Jual</p>
                              <p className="font-black text-[#8C6208]">Rp {fmt(l.harga_jual)}</p>
                            </div>
                          </div>

                          {canEdit && !isSelectionMode && (
                            <div className="flex justify-end gap-1 border-t border-slate-100 pt-3">
                              <Button variant="ghost" size="icon" className="w-8 h-8 hover:bg-slate-100" onClick={() => {
                                setEditLegalitas(l);
                                setLegalitasNama(l.nama);
                                setLegalitasHargaBeli(l.harga_beli);
                                setLegalitasHargaJual(l.harga_jual);
                                setLegalitasCatatan(l.catatan || '');
                                setShowLegalitasForm(true);
                              }}>
                                <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                              </Button>
                              <Button variant="ghost" size="icon" className="w-8 h-8 text-rose-500 hover:bg-rose-50" onClick={async () => {
                                if (await confirm(`Hapus legalitas ${l.nama}?`)) deleteLegalitas.mutate(l.id);
                              }}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 2. Jasa Filling / Packing Section */}
            {(legalitasJasaFilter === 'semua' || legalitasJasaFilter === 'jasa') && (
              <div className="space-y-3">
                <h3 className="text-sm font-black text-slate-800 flex items-center gap-1.5 uppercase tracking-wider">
                  <Sparkles className="w-4 h-4 text-emerald-600" />
                  Biaya Jasa Proses Pengisian & Packing (Per Pcs)
                </h3>
                {jasaFiltered.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 bg-slate-50 border border-slate-200 border-dashed rounded-xl text-xs">
                    Belum ada range harga jasa proses.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {jasaFiltered.map(j => (
                      <Card 
                        key={j.id} 
                        className={`border ${selectedIds.has(j.id) ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200/80'} hover:shadow-md transition-shadow relative overflow-hidden ${selectedIds.has(j.id) ? 'bg-primary/5' : 'bg-white'} ${isSelectionMode ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                        onClick={() => {
                          if (isSelectionMode) toggleSelection(j.id);
                        }}
                      >
                        {isSelectionMode && (
                          <div className="absolute top-3 left-3 z-10" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.has(j.id)}
                              onCheckedChange={() => toggleSelection(j.id)}
                              className="w-5 h-5 rounded-md data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                          </div>
                        )}
                        <div className="absolute top-2 right-2 p-2 text-emerald-600/10">
                          <Sparkles className="w-8 h-8" />
                        </div>
                        <CardContent className="p-5 space-y-4">
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h4 className={`font-extrabold text-sm text-slate-800 ${isSelectionMode ? 'ml-8' : ''}`}>{j.nama}</h4>
                            </div>
                            <span className="inline-block mt-1 bg-emerald-50 text-emerald-800 font-bold border border-emerald-100 px-2 py-0.5 rounded text-[10px]">
                              Range: {j.ukuran_min_ml || 0}ml − {j.ukuran_max_ml || 0}ml
                            </span>
                            <p className="text-[10px] text-slate-400 mt-2 line-clamp-2 italic h-7">{j.catatan || 'Tidak ada catatan teknis.'}</p>
                          </div>

                          <div className="bg-slate-50 rounded-xl p-3 grid grid-cols-2 gap-2 text-xs">
                            <div className="col-span-2">
                              <p className="text-[9px] text-slate-400 font-bold uppercase">Jual Jasa (Profit)</p>
                              <p className="font-black text-emerald-700">Rp {fmt(j.harga_jual)}</p>
                            </div>
                          </div>

                          {canEdit && !isSelectionMode && (
                            <div className="flex justify-end gap-1 border-t border-slate-100 pt-3">
                              <Button variant="ghost" size="icon" className="w-8 h-8 hover:bg-slate-100" onClick={() => {
                                setEditJasa(j);
                                setJasaNama(j.nama);
                                setJasaMinMl(j.ukuran_min_ml);
                                setJasaMaxMl(j.ukuran_max_ml);
                                setJasaHargaBeli(j.harga_beli);
                                setJasaHargaJual(j.harga_jual);
                                setJasaCatatan(j.catatan || '');
                                setShowJasaForm(true);
                              }}>
                                <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                              </Button>
                              <Button variant="ghost" size="icon" className="w-8 h-8 text-rose-500 hover:bg-rose-50" onClick={async () => {
                                if (await confirm(`Hapus range jasa ${j.nama}?`)) deleteJasa.mutate(j.id);
                              }}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        
        {/* PACKAGING PANEL */}
        {activeTab === 'packaging' && (
          packagingFiltered.length === 0 ? (
            <div className="text-center py-16 text-slate-400 border border-slate-200 border-dashed rounded-2xl bg-slate-50/50">
              <Box className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-semibold">Belum ada data packaging.</p>
              <p className="text-xs text-slate-400 mt-1">Klik tombol Tambah di atas untuk mendaftarkan stok baru.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {currentSort.startsWith('vendor') ? (
                Object.entries(groupItemsByVendor(paginatedPackaging)).map(([vendorName, vItems]) => (
                  <div key={vendorName} className="space-y-3">
                    <div className="flex items-center justify-between border-b border-purple-200/80 pb-2">
                      <h3 className="text-xs font-black text-purple-800 flex items-center gap-2 uppercase tracking-wider">
                        <Building2 className="w-4 h-4 text-purple-600" />
                        Vendor: {vendorName}
                      </h3>
                      <span className="text-[10px] font-bold bg-purple-50 text-purple-700 px-2.5 py-0.5 rounded-full border border-purple-200">
                        {vItems.length} item
                      </span>
                    </div>
                    <div className="grid gap-3">
                      {vItems.map(item => renderItemCard(item, item._tipe))}
                    </div>
                  </div>
                ))
              ) : (
                ['botol', 'tutup', 'spray', 'fullset'].map(tipe => {
                  const items = groupedPackaging[tipe] || [];
                  if (items.length === 0) return null;
                  const badge = PACKAGING_TYPE_BADGE[tipe];
                  return (
                    <div key={tipe} className="space-y-3">
                      <h3 className="text-xs font-black text-[#8C6208] flex items-center gap-1.5 uppercase tracking-wider border-b border-slate-200/80 pb-2">
                        <Package2 className="w-4 h-4" />
                        Packaging {badge.label} ({items.length})
                      </h3>
                      <div className="grid gap-3">
                        {items.map(item => renderItemCard(item, item._tipe))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )
        )}

        {/* BAHAN CAIR PANEL */}
        {activeTab === 'bahan' && (
          bahanFiltered.length === 0 ? (
            <div className="text-center py-16 text-slate-400 border border-slate-200 border-dashed rounded-2xl bg-slate-50/50">
              <Box className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-semibold">Belum ada data formula bahan.</p>
              <p className="text-xs text-slate-400 mt-1">Klik tombol Tambah di atas untuk mendaftarkan stok baru.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {currentSort.startsWith('vendor') ? (
                Object.entries(groupItemsByVendor(paginatedBahan)).map(([vendorName, vItems]) => (
                  <div key={vendorName} className="space-y-3">
                    <div className="flex items-center justify-between border-b border-purple-200/80 pb-2">
                      <h3 className="text-xs font-black text-purple-800 flex items-center gap-2 uppercase tracking-wider">
                        <Building2 className="w-4 h-4 text-purple-600" />
                        Vendor: {vendorName}
                      </h3>
                      <span className="text-[10px] font-bold bg-purple-50 text-purple-700 px-2.5 py-0.5 rounded-full border border-purple-200">
                        {vItems.length} item
                      </span>
                    </div>
                    <div className="grid gap-3">
                      {vItems.map(item => renderItemCard(item, 'bahan'))}
                    </div>
                  </div>
                ))
              ) : (
                BAHAN_KATEGORI.map(cat => {
                  const items = groupedBahan[cat] || [];
                  if (items.length === 0) return null;
                  return (
                    <div key={cat} className="space-y-3">
                      <h3 className="text-xs font-black text-blue-700 flex items-center gap-1.5 uppercase tracking-wider border-b border-slate-200/80 pb-2">
                        <Droplets className="w-4 h-4 text-blue-500" />
                        Formula Bahan: {cat} ({items.length})
                      </h3>
                      <div className="grid gap-3">
                        {items.map(item => renderItemCard(item, 'bahan'))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )
        )}

        {/* ITEM TAMBAHAN PANEL */}
        {activeTab === 'item-tambahan' && (
          itemFiltered.length === 0 ? (
            <div className="text-center py-16 text-slate-400 border border-slate-200 border-dashed rounded-2xl bg-slate-50/50">
              <Box className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-semibold">Belum ada data item tambahan.</p>
              <p className="text-xs text-slate-400 mt-1">Klik tombol Tambah di atas untuk mendaftarkan stok baru.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {currentSort.startsWith('vendor') ? (
                Object.entries(groupItemsByVendor(paginatedItemTambahan)).map(([vendorName, vItems]) => (
                  <div key={vendorName} className="space-y-3">
                    <div className="flex items-center justify-between border-b border-purple-200/80 pb-2">
                      <h3 className="text-xs font-black text-purple-800 flex items-center gap-2 uppercase tracking-wider">
                        <Building2 className="w-4 h-4 text-purple-600" />
                        Vendor: {vendorName}
                      </h3>
                      <span className="text-[10px] font-bold bg-purple-50 text-purple-700 px-2.5 py-0.5 rounded-full border border-purple-200">
                        {vItems.length} item
                      </span>
                    </div>
                    <div className="grid gap-3">
                      {vItems.map(item => renderItemCard(item, 'item-tambahan'))}
                    </div>
                  </div>
                ))
              ) : (
                ITEM_TAMBAHAN_KATEGORI.map(cat => {
                  const items = groupedItemTambahan[cat] || [];
                  if (items.length === 0) return null;
                  return (
                    <div key={cat} className="space-y-3">
                      <h3 className="text-xs font-black text-[#8C6208] flex items-center gap-1.5 uppercase tracking-wider border-b border-slate-200/80 pb-2">
                        <Tag className="w-4 h-4" />
                        Kategori Item Tambahan: {cat} ({items.length})
                      </h3>
                      <div className="grid gap-3">
                        {items.map(item => renderItemCard(item, 'item-tambahan'))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )
        )}

        {/* ── Pagination Controls ── */}
        {activeTab !== 'legalitas-jasa' && currentList.length > 0 && (
          <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
              <div className="w-[80px]">
                <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                  <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 25, 50, 100].map(n => (
                      <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-1.5">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
              >
                &lt;
              </button>
              
              {(() => {
                const totalPages = Math.ceil(currentList.length / itemsPerPage);
                const pages = [];
                for(let i=1; i<=totalPages; i++) {
                  if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                          currentPage === i 
                            ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                            : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  } else if (i === currentPage - 2 || i === currentPage + 2) {
                    pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                  }
                }
                return pages;
              })()}
              
              <button 
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(currentList.length / itemsPerPage), p + 1))}
                disabled={currentPage >= Math.ceil(currentList.length / itemsPerPage)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
              >
                &gt;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
      
      {/* MODAL 1: FORM TAMBAH / EDIT LEGALITAS */}
      {showLegalitasForm && (
        <div className="fixed inset-0 bg-slate-900/60  z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-[#F9F5EC]">
              <h3 className="font-black text-[#8C6208] text-sm uppercase tracking-wider">
                {editLegalitas ? 'Edit Biaya Legalitas' : 'Tambah Biaya Legalitas'}
              </h3>
              <button onClick={() => setShowLegalitasForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Nama Layanan Legalitas *</label>
                <select 
                  value={legalitasNama} 
                  onChange={e => setLegalitasNama(e.target.value)} 
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-xs"
                >
                  <option value="" disabled>Pilih Layanan Legalitas</option>
                  <option value="BPOM (Per Varian)">BPOM (Per Varian)</option>
                  <option value="HAKI (Hak Kekayaan Intelektual)">HAKI (Hak Kekayaan Intelektual)</option>
                  <option value="Sertifikasi Halal">Sertifikasi Halal</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">Harga Beli HPP *</label>
                  <Input type="number" value={legalitasHargaBeli} onChange={e => setLegalitasHargaBeli(e.target.value)} placeholder="Contoh: 500000" className="text-xs" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">Harga Jual Client *</label>
                  <Input type="number" value={legalitasHargaJual} onChange={e => setLegalitasHargaJual(e.target.value)} placeholder="Contoh: 2500000" className="text-xs" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Catatan Teknis / Deskripsi</label>
                <Input value={legalitasCatatan} onChange={e => setLegalitasCatatan(e.target.value)} placeholder="Contoh: Proses pengurusan resmi BPOM 3-6 bulan" className="text-xs" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
              <Button variant="ghost" size="sm" className="text-xs font-bold" onClick={() => setShowLegalitasForm(false)}>Batal</Button>
              <Button size="sm" className="bg-[#302621] text-white hover:bg-[#1A1411] text-xs font-bold" onClick={() => {
                if (!legalitasNama || !legalitasHargaBeli || !legalitasHargaJual) {
                  toast.error('Mohon isi semua field wajib!');
                  return;
                }
                const payload = {
                  nama: legalitasNama,
                  harga_beli: parseFloat(legalitasHargaBeli) || 0,
                  harga_jual: parseFloat(legalitasHargaJual) || 0,
                  catatan: legalitasCatatan
                };
                if (editLegalitas) {
                  updateLegalitas.mutate({ id: editLegalitas.id, data: payload });
                } else {
                  addLegalitas.mutate(payload);
                }
                setShowLegalitasForm(false);
              }}>
                Simpan
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: FORM TAMBAH / EDIT RANGE JASA */}
      {showJasaForm && (
        <div className="fixed inset-0 bg-slate-900/60  z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-[#F9F5EC]">
              <h3 className="font-black text-[#8C6208] text-sm uppercase tracking-wider">
                {editJasa ? 'Edit Range Jasa Proses' : 'Tambah Range Jasa Proses'}
              </h3>
              <button onClick={() => setShowJasaForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Nama Jasa Proses *</label>
                <Input value={jasaNama} onChange={e => setJasaNama(e.target.value)} placeholder="Contoh: Jasa Ukuran ≤ 30ml" className="text-xs" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">Ukuran Min (ml) *</label>
                  <Input type="number" value={jasaMinMl} onChange={e => setJasaMinMl(e.target.value)} placeholder="Contoh: 11" className="text-xs" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">Ukuran Max (ml) *</label>
                  <Input type="number" value={jasaMaxMl} onChange={e => setJasaMaxMl(e.target.value)} placeholder="Contoh: 30" className="text-xs" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Jual Jasa Proses (Pcs) *</label>
                <Input type="number" value={jasaHargaJual} onChange={e => setJasaHargaJual(e.target.value)} placeholder="Contoh: 10000" className="text-xs" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Catatan Deskripsi</label>
                <Input value={jasaCatatan} onChange={e => setJasaCatatan(e.target.value)} placeholder="Contoh: Berlaku untuk serum dan cairan semi-kental standard" className="text-xs" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
              <Button variant="ghost" size="sm" className="text-xs font-bold" onClick={() => setShowJasaForm(false)}>Batal</Button>
              <Button size="sm" className="bg-[#302621] text-white hover:bg-[#1A1411] text-xs font-bold" onClick={() => {
                if (!jasaNama || !jasaMinMl || !jasaMaxMl || !jasaHargaJual) {
                  toast.error('Mohon isi semua field wajib!');
                  return;
                }
                const payload = {
                  nama: jasaNama,
                  ukuran_min_ml: parseFloat(jasaMinMl) || 0,
                  ukuran_max_ml: parseFloat(jasaMaxMl) || 0,
                  harga_beli: 0,
                  harga_jual: parseFloat(jasaHargaJual) || 0,
                  catatan: jasaCatatan
                };
                if (editJasa) {
                  updateJasa.mutate({ id: editJasa.id, data: payload });
                } else {
                  addJasa.mutate(payload);
                }
                setShowJasaForm(false);
              }}>
                Simpan
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Forms default */}
      {showForm && activeTab === 'packaging' && editTipe !== 'fullset' && (
        <PackagingForm
          item={editItem}
          tipeAwal={editTipe}
          kurs={kurs}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSaved={handleSaved}
          hidePrices={isProductionView}
        />
      )}
      {showForm && activeTab === 'packaging' && editTipe === 'fullset' && (
        <FullsetForm
          item={editItem}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSaved={handleSaved}
          botolList={botolList}
          tutupList={tutupList}
          sprayList={sprayList}
        />
      )}
      {showForm && activeTab === 'bahan' && (
        <StokBahanForm
          item={editItem}
          kategoriAwal={bahanFilter !== 'semua' ? bahanFilter : ''}
          kurs={kurs}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSaved={handleSaved}
          hidePrices={isProductionView}
        />
      )}
      {showForm && activeTab === 'item-tambahan' && (
        <ItemTambahanForm
          item={editItem}
          kategoriAwal={itemFilter !== 'semua' ? itemFilter : ''}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSaved={handleSaved}
          hidePrices={isProductionView}
        />
      )}
      
      <BatchActionBar 
        isSelectionMode={isSelectionMode}
        selectedCount={selectedIds.size}
        totalCount={currentList.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={handleBatchDelete}
        isDeleting={delBotol.isPending || delTutup.isPending || delSpray.isPending || delBahan.isPending || delItem.isPending || deleteLegalitas.isPending || deleteJasa.isPending}
      />

      {/* Modal Cetak Label Sample Stok & Kedatangan */}
      {showCetakSampleModal && (
        <CetakSampleStokModal
          isOpen={showCetakSampleModal}
          onClose={() => {
            setShowCetakSampleModal(false);
            setSampleModalInitialItem(null);
          }}
          allStockData={{
            bahanList,
            botolList,
            tutupList,
            sprayList,
            fullsetList,
            itemTambahanList
          }}
          initialItem={sampleModalInitialItem}
        />
      )}
    </div>
  );
}
